import React, { useState, useEffect } from 'react';

const DelIcon = () => (
  <svg viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

/* ── URL-Overlay ── */
function StepUrlOverlay({ stepNum, initialUrls, onOk, onCancel }) {
  const [urls, setUrls] = useState(
    initialUrls.length > 0 ? initialUrls : ['']
  );

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  function setUrl(i, val) {
    setUrls(prev => { const n = [...prev]; n[i] = val; return n; });
  }

  function removeUrl(i) {
    setUrls(prev => prev.filter((_, idx) => idx !== i));
  }

  function addUrl() {
    setUrls(prev => [...prev, '']);
  }

  function handleOk() {
    onOk(urls.filter(u => u.trim() !== ''));
  }

  return (
    <div className="step-overlay-backdrop" onMouseDown={onCancel}>
      <div className="step-overlay" onMouseDown={e => e.stopPropagation()}>
        <div className="step-overlay-title">Bilder für Schritt {stepNum}</div>

        <div className="step-overlay-list">
          {urls.map((url, i) => (
            <div key={i} className="step-overlay-entry">
              <div className="step-overlay-row">
                <input
                  className="step-overlay-input"
                  type="url"
                  placeholder="https://… Bild-URL"
                  value={url}
                  autoFocus={i === 0}
                  onChange={e => setUrl(i, e.target.value)}
                />
                <button
                  type="button"
                  className="step-overlay-del"
                  onClick={() => removeUrl(i)}
                  title="Entfernen"
                >×</button>
              </div>
              {url.trim() && (
                <img
                  src={url.trim()}
                  alt=""
                  className="step-overlay-preview"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                  onLoad={e => { e.currentTarget.style.display = 'block'; }}
                />
              )}
            </div>
          ))}
        </div>

        <button type="button" className="step-overlay-add" onClick={addUrl}>
          + Bild-URL hinzufügen
        </button>

        <div className="step-overlay-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Abbrechen</button>
          <button type="button" className="btn btn-primary" onClick={handleOk}>OK</button>
        </div>
      </div>
    </div>
  );
}

export default function StepsEditor({
  steps,
  stepImagePreviews,
  stepImageInputRefs,
  dragOverIdx,
  canDrag,
  onStepChange,
  onStepImageChange,
  onRemoveStepImage,
  onAddStep,
  onRemoveStep,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onStepImagesChange,
}) {
  const [overlayIdx, setOverlayIdx] = useState(null);

  function openOverlay(idx) { setOverlayIdx(idx); }
  function closeOverlay() { setOverlayIdx(null); }

  function handleOk(idx, urls) {
    onStepImagesChange(idx, urls);
    closeOverlay();
  }

  return (
    <>
      {overlayIdx !== null && (
        <StepUrlOverlay
          stepNum={overlayIdx + 1}
          initialUrls={steps[overlayIdx]?.images || []}
          onOk={urls => handleOk(overlayIdx, urls)}
          onCancel={closeOverlay}
        />
      )}

      <div className="editor-section">
        <div className="editor-section-head">Zubereitung</div>
        <div className="editor-section-body">
          {steps.map((step, idx) => {
            const preview = stepImagePreviews[idx] || step.image_path || null;
            const urlImages = step.images || [];
            const hasUrlImages = urlImages.length > 0;

            return (
              <div
                key={idx}
                className={'step-editor-row' + (dragOverIdx === idx ? ' step-drag-over' : '')}
                draggable
                onDragStart={e => onDragStart(e, idx)}
                onDragOver={e => onDragOver(e, idx)}
                onDrop={e => onDrop(e, idx)}
                onDragEnd={onDragEnd}
              >
                <span
                  className="step-handle"
                  title="Ziehen zum Sortieren"
                  onMouseDown={() => { canDrag.current = true; }}
                  onMouseUp={() => { canDrag.current = false; }}
                >⠿</span>
                <span className="step-e-num">{idx + 1}</span>
                <div style={{ flex: 1 }}>
                  <textarea
                    className="step-e-textarea"
                    value={step.text || ''}
                    placeholder="Schritt beschreiben …"
                    onChange={e => onStepChange(idx, e.target.value)}
                  />
                  {hasUrlImages && (
                    <div className="step-img-thumbs">
                      {urlImages.map((src, i) => (
                        <div key={i} className="step-img-thumb-wrap">
                          <img
                            src={src}
                            alt=""
                            className="step-img-thumb"
                            onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                          />
                          <button
                            type="button"
                            className="step-img-thumb-del"
                            onClick={() => onStepImagesChange(idx, urlImages.filter((_, j) => j !== i))}
                            title="Bild entfernen"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {preview && (
                    <div className="step-img-preview">
                      <img src={preview} alt={`Schritt ${idx + 1}`} />
                      <button type="button" className="step-img-remove" onClick={() => onRemoveStepImage(idx)} title="Bild entfernen">×</button>
                    </div>
                  )}
                </div>
                <div className="step-actions">
                  <button type="button" className="step-action-btn" title="Foto hochladen"
                    onClick={() => stepImageInputRefs.current[idx]?.click()}>📷</button>
                  <button
                    type="button"
                    className={`step-action-btn${hasUrlImages ? ' active' : ''}`}
                    title="Bild-URLs verwalten"
                    onClick={() => openOverlay(idx)}
                  >
                    <LinkIcon />
                    {hasUrlImages && <span className="step-url-count">{urlImages.length}</span>}
                  </button>
                  <input
                    type="file" accept="image/*"
                    style={{ display: 'none' }}
                    ref={el => stepImageInputRefs.current[idx] = el}
                    onChange={e => onStepImageChange(idx, e.target.files?.[0])}
                  />
                </div>
                <button type="button" className="step-e-del" onClick={() => onRemoveStep(idx)} title="Schritt löschen">
                  <DelIcon />
                </button>
              </div>
            );
          })}
          <button type="button" className="add-ing-btn" onClick={onAddStep}>+ Schritt hinzufügen</button>
        </div>
      </div>
    </>
  );
}
